import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCog,
  FileText,
  Calendar,
  CalendarDays,
  BookOpen,
  Newspaper,
  Landmark,
  PenTool,
  FileSignature,
  Briefcase,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { LOGOS } from "../lib/ablemp-data";
import { isAdminRole } from "../lib/roles";

const ADMIN_NAV = [
  { to: "/admin/dashboard", label: "Painel", icon: LayoutDashboard },
  {
    label: "Associados",
    icon: Users,
    group: "associados",
    children: [
      { to: "/admin/associados", label: "Lista de associados" },
      { to: "/admin/associados/novo", label: "Novo associado" },
      { to: "/admin/tipos-associados", label: "Tipos & Contribuições" },
    ],
  },
  {
    to: "/admin/usuarios",
    label: "Conselheiros e Diretores",
    icon: UserCog,
  },
  { to: "/admin/arquivos", label: "Arquivos", icon: FileText },
  {
    label: "Comercial",
    icon: Briefcase,
    group: "comercial",
    children: [
      { to: "/admin/comercial/dashboard", label: "Dashboard" },
      { to: "/admin/comercial/kanban", label: "Funil (Kanban)" },
      { to: "/admin/comercial/leads", label: "Todos os leads" },
      { to: "/admin/comercial/leads/novo", label: "Indicar associado" },
      { to: "/admin/comercial/estagios", label: "Estágios" },
      { to: "/admin/comercial/segmentos", label: "Segmentos" },
    ],
  },
  {
    label: "Financeiro",
    icon: Landmark,
    group: "financeiro",
    children: [
      { to: "/admin/recibos", label: "Recibos" },
      { to: "/admin/relatorios", label: "Relatórios" },
      { to: "/admin/despesas", label: "Restituição de Despesas" },
    ],
  },
  {
    label: "Assinaturas",
    icon: FileSignature,
    group: "assinaturas",
    children: [
      { to: "/admin/minha-assinatura", label: "Minha Assinatura" },
      { to: "/admin/assinaturas", label: "Assinador & Verificador" },
    ],
  },
  { to: "/admin/noticias", label: "Notícias e Artigos", icon: Newspaper },
  { to: "/admin/reunioes", label: "Reuniões", icon: Calendar },
  { to: "/admin/eventos", label: "Eventos", icon: CalendarDays },
  { to: "/admin/estatuto", label: "Estatuto & Ética", icon: BookOpen },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings },
];

const ASSOC_NAV = [
  { to: "/portal/dashboard", label: "Painel", icon: LayoutDashboard },
  { to: "/portal/meus-arquivos", label: "Meus Arquivos", icon: FileText },
  { to: "/portal/meus-dados", label: "Meus Dados", icon: UserCog },
  { to: "/portal/reunioes", label: "Reuniões", icon: Calendar },
  { to: "/portal/estatuto", label: "Estatuto & Ética", icon: BookOpen },
];

const INTERNAL_NAV = [
  { to: "/portal/dashboard", label: "Painel", icon: LayoutDashboard },
  { to: "/portal/meus-arquivos", label: "Arquivos", icon: FileText },
  { to: "/portal/despesas", label: "Restituição de Despesas", icon: Landmark },
  {
    label: "Comercial",
    icon: Briefcase,
    group: "comercial",
    children: [
      { to: "/portal/comercial/dashboard", label: "Dashboard" },
      { to: "/portal/comercial/kanban", label: "Funil (Kanban)" },
      { to: "/portal/comercial/leads", label: "Todos os leads" },
      { to: "/portal/comercial/leads/novo", label: "Indicar associado" },
    ],
  },
  {
    label: "Assinaturas",
    icon: FileSignature,
    group: "assinaturas",
    children: [
      { to: "/portal/minha-assinatura", label: "Minha Assinatura" },
      { to: "/portal/assinador", label: "Assinador & Verificador" },
    ],
  },
  { to: "/portal/reunioes", label: "Reuniões", icon: Calendar },
  { to: "/portal/estatuto", label: "Estatuto & Ética", icon: BookOpen },
];

const ROLE_LABELS = {
  admin: "Administrador",
  diretor_financeiro: "Diretor Financeiro",
  presidente: "Presidente",
  associado: "Associado",
  conselheiro: "Conselheiro",
  diretor: "Diretor",
};

export const PortalLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  if (!user) return null;
  let items = ASSOC_NAV;
  if (isAdminRole(user.role)) items = ADMIN_NAV;
  else if (user.role === "conselheiro" || user.role === "diretor")
    items = INTERNAL_NAV;
  const roleLabel = ROLE_LABELS[user.role] || "Associado";
  const baseHome = isAdminRole(user.role)
    ? "/admin/dashboard"
    : "/portal/dashboard";

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const isGroupActive = (group) =>
    group.children?.some((c) => location.pathname.startsWith(c.to));

  const toggleGroup = (key) =>
    setOpenGroups((s) => ({ ...s, [key]: !s[key] }));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      {/* Sidebar */}
      <aside
        data-testid="portal-sidebar"
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-[#0A0F14] text-white transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex items-center justify-between h-20 px-6 border-b border-white/10">
          <Link to={baseHome} className="flex items-center gap-3">
            <img src={LOGOS.white} alt="ABLEMP" className="h-12 w-auto" />
          </Link>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(false)}
            aria-label="Fechar menu"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-5 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">
            {roleLabel}
          </div>
          <div className="mt-1 font-serif-display text-lg leading-tight">
            {user.name}
          </div>
          <div className="text-[11px] text-white/55 truncate">{user.email}</div>
        </div>
        <nav className="px-3 py-4 space-y-1">
          {items.map((it) => {
            const Icon = it.icon;
            if (it.children) {
              const expanded =
                openGroups[it.group] ?? isGroupActive(it);
              const groupActive = isGroupActive(it);
              return (
                <div key={it.group}>
                  <button
                    type="button"
                    data-testid={`sidebar-group-${it.group}`}
                    onClick={() => toggleGroup(it.group)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                      groupActive
                        ? "bg-white/10 text-white"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Icon size={16} strokeWidth={1.6} />
                    <span className="flex-1 text-left">{it.label}</span>
                    {expanded ? (
                      <ChevronDown size={14} className="opacity-60" />
                    ) : (
                      <ChevronRight size={14} className="opacity-60" />
                    )}
                  </button>
                  {expanded && (
                    <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
                      {it.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`sidebar-link-${c.to.split("/").pop()}`}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-2 rounded-sm text-[13px] transition-colors ${
                              isActive
                                ? "bg-white/10 text-white"
                                : "text-white/65 hover:text-white hover:bg-white/5"
                            }`
                          }
                        >
                          <span className="h-1 w-1 rounded-full bg-current opacity-50" />
                          <span className="flex-1">{c.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`sidebar-link-${it.to.split("/").pop()}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.6} />
                <span className="flex-1">{it.label}</span>
                <ChevronRight size={14} className="opacity-40" />
              </NavLink>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 px-3 py-4 border-t border-white/10">
          <button
            data-testid="sidebar-logout"
            onClick={doLogout}
            className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 text-sm text-white/85 hover:text-white hover:bg-white/5 rounded-sm"
          >
            <LogOut size={16} strokeWidth={1.6} />
            Sair
          </button>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header
          data-testid="portal-topbar"
          className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-5 lg:px-8"
        >
          <button
            className="lg:hidden text-slate-700 p-2"
            onClick={() => setOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu size={20} />
          </button>
          <div className="hidden lg:block">
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
              {roleLabel}
            </div>
            <div className="font-serif-display text-lg leading-none">
              {isAdminRole(user.role)
                ? "Painel Administrativo"
                : user.role === "conselheiro" || user.role === "diretor"
                ? "Portal Institucional"
                : "Portal do Associado"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium">{user.name}</div>
              <div className="text-[11px] text-slate-500">{user.email}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-[#002776] text-white grid place-items-center text-sm font-semibold">
              {user.name?.slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>

        <main className="flex-1 p-5 lg:p-8 max-w-[1400px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PortalLayout;
